/**
 * SSOCallback.js — Handles SSO redirect callbacks
 *
 * Mounted at /auth/google and /auth/microsoft.
 * After Microsoft OAuth2 redirects back here with ?code=..., this page:
 *   1. Extracts the code from the URL
 *   2. Posts it to the backend (/api/auth/sso/microsoft)
 *   3. Redirects the user to their intended destination
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 *           THIS BREAKS THE AUTH. Always use window.location.origin.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';

export default function SSOCallback({ provider }) {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const { loginWithSSO } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const code  = searchParams.get('code');
    const state = searchParams.get('state');
    const err   = searchParams.get('error');

    if (err) {
      setError(`SSO error: ${searchParams.get('error_description') || err}`);
      return;
    }

    if (!code) {
      setError('No authorization code received.');
      return;
    }

    // The state we sent must come back unchanged, otherwise this is not our sign-in
    const expectedState = sessionStorage.getItem('sso_state');
    sessionStorage.removeItem('sso_state');
    if (!expectedState || state !== expectedState) {
      setError('Sign-in could not be verified (state mismatch). Please start again from the sign-in page.');
      return;
    }

    const redirectTo = sessionStorage.getItem('sso_redirect') || '/admin';
    sessionStorage.removeItem('sso_redirect');

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + `/auth/${provider}`;

    api.post(`/auth/sso/${provider}`, { code, redirect_uri: redirectUri, portal: redirectTo.split('/')[1] || undefined })
      .then(async ({ data }) => {
        if (data.requires_mfa) {
          // PRD: SSO continues with the emailed 6-digit code on the portal's sign-in page
          navigate(`${redirectTo}/login`, { replace: true, state: { codeStep: data } });
        } else if (data.user) {
          await loginWithSSO(data);
          navigate(redirectTo, { replace: true });
        } else {
          setError('Authentication failed — no token received.');
        }
      })
      .catch(e => {
        setError(e.response?.data?.detail || 'SSO authentication failed.');
      });
  }, []);

  if (error) {
    return (
      <div className="fv" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="panel v g16" style={{ maxWidth: 400, padding: 32, textAlign: 'center' }}>
          <h2 className="t2" style={{ color: 'var(--bad)' }}>Sign-in failed</h2>
          <p className="ts muted">{error}</p>
          <button className="btn p" onClick={() => navigate(-1)}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fv" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="v g12" style={{ alignItems: 'center' }}>
        <div style={{ width: 32, height: 32, border: '3px solid var(--brand)', borderTopColor: 'transparent',
          borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p className="ts muted">Completing sign-in…</p>
      </div>
    </div>
  );
}
